import type { Page, PageNumber } from "@pdfking/renderer-api"
import type { Engine } from "@pdfking/taskmaker"
import type { AsyncSequence, Partial, Sequence } from "@pdfking/tfunc"
import type { PdfEngine, TaskIDOptions } from "../types/engine/PdfEngine"
import type { PdfEngineOptions } from "../types/engine/PdfEngineOptions"
import type { GeneratedFile } from "../types/files/cached/GeneratedFile"
import type { RemoteFile } from "../types/files/cached/RemoteFile"
import type { RemoteFileChunk } from "../types/files/cached/RemoteFileChunk"
import type { SinglePageImage } from "../types/files/cached/SinglePageImage"
import type { SinglePagePdf } from "../types/files/cached/SinglePagePdf"
import type { VirtualFile } from "../types/files/VirtualFile"
import type { EngineTasks } from "../types/tasks/EngineTasks"
import type { FilePlottingContext } from "../types/tasks/FilePlottingContext"
import { Ceil, Max, Min } from "@pdfking/math"
import {
  ArrayToIterable,
  First,
  IsArray,
  JoinConcurrent,
  MapAsync,
  Map as MapSync,
  MethodDispatcher,
  Peek,
  Range,
  Values,
  Void,
} from "@pdfking/tfunc"
import { CreateRemoteFile } from "../files/CreateRemoteFile"
import { GenerateFile } from "../files/GenerateFile"
import { RequestBlob } from "../files/RequestBlob"
import { VirtualFileType } from "../types/files/VirtualFileType"
import { CreatePdfEngineOptions } from "./CreatePdfEngineOptions"

export const CreateEngineInternal = (
  tasks: Engine<EngineTasks>,
  config: Partial<PdfEngineOptions>
): PdfEngine => {
  const options = CreatePdfEngineOptions(config)

  const mapSinglePage = <T>(
    processor: (page: SinglePagePdf) => AsyncSequence<T>,
    files: Sequence<VirtualFile>
  ): AsyncGenerator<T> =>
    JoinConcurrent(
      ...MapSync(file => MapAsync(processor, SplitPdf(file)), files)
    )

  const innerSplit = async function* (
    source: GeneratedFile | RemoteFile,
    { cache_evict = false, task_id }: TaskIDOptions = {}
  ): AsyncGenerator<SinglePagePdf> {
    const max_pages = options.max_split_pages

    const runSplit = async function* (
      start_page: PageNumber,
      end_page: PageNumber
    ): AsyncGenerator<SinglePagePdf> {
      yield* tasks.TaskSplit({
        cache_evict,
        end_page,
        options,
        source,
        start_page,
        task_id,
      })
    }

    const {
      stream,
      value: first_page,
      error,
    } = await Peek(runSplit(1 as PageNumber, max_pages))

    if (error) throw error

    if (first_page) {
      const batch_count = Ceil((first_page.page_count - max_pages) / max_pages)

      if (batch_count > 0) {
        const remaining_batches = MapSync(batch_index => {
          const start_page = (max_pages +
            batch_index * max_pages +
            1) as PageNumber
          const end_page = Min(
            start_page + max_pages - 1,
            first_page.page_count
          ) as PageNumber

          return runSplit(start_page, end_page)
        }, Range(batch_count))

        yield* JoinConcurrent([stream, ...remaining_batches])
      } else {
        yield* stream
      }
    }
  }

  const SplitPdf = (
    file: VirtualFile,
    opts?: TaskIDOptions
  ): AsyncGenerator<SinglePagePdf> => {
    const dispatcher = MethodDispatcher<
      [VirtualFile],
      AsyncGenerator<SinglePagePdf>
    >({
      [VirtualFileType.GENERATED_FILE]: p => innerSplit(p, opts),
      [VirtualFileType.MULTI_PAGE_PDF]: p => ArrayToIterable(p.pages),
      [VirtualFileType.REMOTE_FILE]: p => innerSplit(p, opts),
      [VirtualFileType.SINGLE_PAGE_IMAGE]: p => ArrayToIterable([p.source]),
      [VirtualFileType.SINGLE_PAGE_PDF]: p => ArrayToIterable([p]),
      [VirtualFileType.ZIP_FILE]: p => mapSinglePage(p => [p], Values(p.files)),
    })
    return dispatcher(file)
  }

  const rasterizeSingle = async function* (
    source: SinglePagePdf,
    { cache_evict = false, task_id }: TaskIDOptions = {}
  ): AsyncGenerator<SinglePageImage> {
    yield* tasks.TaskRasterize({ cache_evict, options, source, task_id })
  }

  const RasterizePdf = (
    file: VirtualFile,
    opts?: TaskIDOptions
  ): AsyncGenerator<SinglePageImage> => {
    const dispatcher = MethodDispatcher<
      [VirtualFile],
      AsyncGenerator<SinglePageImage>
    >(
      {
        [VirtualFileType.MULTI_PAGE_PDF]: p =>
          mapSinglePage(p => rasterizeSingle(p, opts), p.pages),
        [VirtualFileType.SINGLE_PAGE_IMAGE]: p => ArrayToIterable([p]),
        [VirtualFileType.SINGLE_PAGE_PDF]: p => rasterizeSingle(p, opts),
      },
      p => mapSinglePage(p => rasterizeSingle(p, opts), [p])
    )
    return dispatcher(file)
  }

  const separateSingle = async function* (
    source: SinglePagePdf,
    { cache_evict = false, task_id }: TaskIDOptions = {}
  ): AsyncGenerator<SinglePageImage> {
    yield* tasks.TaskSeparate({ cache_evict, options, source, task_id })
  }

  const SeparatePdf = (
    file: VirtualFile,
    task_options?: TaskIDOptions
  ): AsyncGenerator<SinglePageImage> => {
    const dispatcher = MethodDispatcher<
      [VirtualFile],
      AsyncGenerator<SinglePageImage>
    >(
      {
        [VirtualFileType.MULTI_PAGE_PDF]: p =>
          mapSinglePage(p => separateSingle(p, task_options), p.pages),
        [VirtualFileType.SINGLE_PAGE_IMAGE]: p => ArrayToIterable([p]),
        [VirtualFileType.SINGLE_PAGE_PDF]: p => separateSingle(p, task_options),
      },
      p => mapSinglePage(p => separateSingle(p, task_options), [p])
    )
    return dispatcher(file)
  }

  const MergePdf = async (
    files: AsyncSequence<[PageNumber, PageNumber, SinglePagePdf] | VirtualFile>,
    { cache_evict = false, task_id }: TaskIDOptions = {}
  ) => {
    let page_index = 0 as PageNumber

    const pages = async function* () {
      for await (const page of files) {
        if (IsArray(page)) {
          const [pn, pc, content] = page as [
            PageNumber,
            PageNumber,
            SinglePagePdf,
          ]
          page_index = Max(page_index, pc) as PageNumber
          yield { content, page_count: pc, page_number: pn }
        } else {
          const page_count = page_index
          for await (const pdf of SplitPdf(page)) {
            page_index = (page_index + 1) as PageNumber
            yield {
              content: pdf,
              page_count: (page_count + pdf.page_count) as PageNumber,
              page_number: page_index,
            }
          }
        }
      }
    }

    return First(tasks.TaskMerge({ cache_evict, options, task_id }, pages()))
  }

  const ZipFiles = async (
    files: AsyncSequence<[string, VirtualFile]>,
    { cache_evict = false, task_id }: TaskIDOptions = {}
  ) =>
    First(
      tasks.TaskZip(
        { cache_evict, options, task_id },
        MapAsync(([name, file]) => ({ content: file, name }), files)
      )
    )

  const PlotPdf = async (
    page: Page,
    context: FilePlottingContext = {},
    { cache_evict = false, task_id }: TaskIDOptions = {}
  ): Promise<SinglePagePdf> =>
    First(tasks.TaskPlot({ cache_evict, context, options, page, task_id }))

  const DownloadFile = async function* (
    source: RemoteFile,
    { cache_evict = false, task_id }: TaskIDOptions = {}
  ): AsyncGenerator<RemoteFileChunk> {
    yield* tasks.TaskDownload({ cache_evict, options, source, task_id })
  }

  return {
    CreateRemoteFile,
    config: options,
    DownloadFile,
    GenerateFile: (stream, content_type) =>
      GenerateFile(tasks, options, stream, content_type, Void),
    MergePdf,
    PlotPdf,
    RasterizePdf,
    RequestBlob: (file, emit) => RequestBlob(tasks, options, file, emit),
    SeparatePdf,
    SplitPdf,
    ZipFiles,
  }
}
